import { Component, OnInit, AfterViewInit, OnChanges, SimpleChanges, Output, EventEmitter, input, inject } from '@angular/core';
import {
  trigger,
  style,
  animate,
  transition
} from '@angular/animations';
import { TreeProtectionService } from '../tree-protection.service';
import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToPrecisionPipe } from 'src/app/pipes/to-precision.pipe';


interface IObjectKeys {
  [key: string]: string | number | null | IObjectKeys;
}

interface summaryNumberOfRollsInterface {
  standarddWire: number,
  barbWire: number,
  wallabyProofMesh: number,
  star: number,
  steelWood: number
}


@Component({
    selector: 'app-tree-protection',
    templateUrl: './tree-protection.component.html',
    animations: [
      trigger('inOutAnimation', [
        transition(':enter', [
          style({ opacity: 0 }),
          animate('300ms ease-out', style({ opacity: 1 }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ opacity: 0 }))
        ])
      ])
    ],
    styleUrls: ['./tree-protection.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule,ToPrecisionPipe]
})

export class TreeProtectionComponent implements OnInit, OnChanges {
  treeProtectionService = inject(TreeProtectionService);


  @Output() parentEventTransmitter = new EventEmitter();
  readonly fenceSize = input<any>(undefined);

  fencingMaterialTableInformation: IObjectKeys = {
    "Cattle-proof": {
      "Fencing Requirements": "Cattle-proof",
      "Timing": "Cattle need to be kept out of trees until they reach about 6m in height",
      "Other Considerations": "The minimum heights are a guide only and that excessive stocking rates and the length of the period which the stock is within the trees can cause detrimental impacts to your plantations health",
      "Standard Wire Rows Required": 0,
      "Barb Wire Rows Required": 5,
      "Standard Wire Roll length - metres": 200,
      "Barb Wire Roll Length - metres": 500,
      "Wallaby Proof Mesh Roll Length - metres": 0,
    },
    "Sheep-proof": {
      "Fencing Requirements": "Sheep-proof",
      "Timing": "Sheep need to be kept out of trees until they reach about 4m in height",
      "Other Considerations": "The minimum heights are a guide only and that excessive stocking rates and the length of the period which the stock is within the trees can cause detrimental impacts to your plantations health.",
      "Standard Wire Rows Required": 4,
      "Barb Wire Rows Required": 1,
      "Standard Wire Roll length - metres": 200,
      "Barb Wire Roll Length - metres": 500,
      "Wallaby Proof Mesh Roll Length - metres": 0,
    },
    "Wallaby-proof": {
      "Fencing Requirements": "Wallaby-proof",
      "Timing": "Wallabies need to be kept out of trees until they reach over 2m in height, approximately 2-4 years of age depending on how well the trees are growing. ",
      "Other Considerations": "Wallabies can also be controlled via shooting. Shooting should be done for 12 months after planting. Sites which have high wallaby pressure and a lower site productivity, browsing control measures may need to be done for 12-24 months.",
      "Standard Wire Rows Required": 0,
      "Barb Wire Rows Required": 1,
      "Standard Wire Roll length - metres": 200,
      "Barb Wire Roll Length - metres": 500,
      "Wallaby Proof Mesh Roll Length - metres": 30,
    },
  }

  materialTreeProtectionForm: FormGroup = new FormGroup({
    lengthOfFenceRequired: new FormControl({ value: null, disabled: true }, [Validators.required]),
    postSpacing: new FormControl(3, [Validators.minLength(0)]),
    ratioStarPostToSteel: new FormControl(3, [Validators.minLength(0)]),
    standardWireRows: new FormControl(0, [Validators.minLength(0)]),
    barbWireRows: new FormControl(0, [Validators.minLength(0)]),
  });

  fencingType: string[] = [];
  selectedFencingType?: string | null;
  selectedFencingTypeDetails?: any;

  summaryNumberOfRollsRequired: summaryNumberOfRollsInterface = {
    standarddWire: 0,
    barbWire: 10,
    wallabyProofMesh: 100,
    star: 0,
    steelWood: 0
  };

  ngOnInit(): void {

    // Generating keys for the fencing type dropdown list.
    this.fencingType = Object.keys(this.fencingMaterialTableInformation);

  }

  ngOnChanges(changes: SimpleChanges): void {

     this.materialTreeProtectionForm.controls["lengthOfFenceRequired"].setValue(Number(this.fenceSize().toFixed(2)));

     this.materialTreeProtectionForm.controls["lengthOfFenceRequired"].updateValueAndValidity()
  }

  changeToFencingTypeHandler(evt: Event) {
    this.selectedFencingType = null;

    const elem = evt.target as HTMLInputElement;
    const val = elem.value;

    if (!val) {
      this.selectedFencingType = null;
      this.materialTreeProtectionForm.controls["lengthOfFenceRequired"].disable();
    } else {
      this.materialTreeProtectionForm.controls["lengthOfFenceRequired"].enable();
    }

    // Timeout has been set to trigger fade in animation on the tree-protection component.

    setTimeout(() => {
      this.selectedFencingType = val;
      this.selectedFencingTypeDetails = this.fencingMaterialTableInformation[this.selectedFencingType];

      this.populateFormDataHandler(this.fencingMaterialTableInformation[this.selectedFencingType]);
    }, 200)
  }

  populateFormDataHandler(tableObjectData: any) {
    this.materialTreeProtectionForm.controls["standardWireRows"].setValue(tableObjectData["Standard Wire Rows Required"]);
    this.materialTreeProtectionForm.controls["barbWireRows"].setValue(tableObjectData["Barb Wire Rows Required"]);

    // Passing data down to the summary table.
    this.populateSummaryTableData(tableObjectData);
  }

  // Populating summary table data handler
  populateSummaryTableData(tableObjectData: any) {

    const isWallabyProof: boolean = tableObjectData["Fencing Requirements"].toLowerCase().includes("wallaby"); // C13

    const lengthOfTheFence = this.materialTreeProtectionForm.get("lengthOfFenceRequired")?.value; // C14

    const ratioStarPostsToSteelPine = Number(this.materialTreeProtectionForm.get("ratioStarPostToSteel")?.value); // C18
    const standardWireRowRequired = this.materialTreeProtectionForm.get("standardWireRows")?.value; // C19
    const barbWireRequired = this.materialTreeProtectionForm.get("barbWireRows")?.value; // C20

    const barbWireRollLength = tableObjectData["Barb Wire Roll Length - metres"]; // C29
    const standardWireRollLength = tableObjectData["Standard Wire Roll length - metres"]; // B29
    const wallabyProofMeshLength = tableObjectData["Wallaby Proof Mesh Roll Length - metres"]; // D29


    const starValue: number = ((lengthOfTheFence / (ratioStarPostsToSteelPine + 1)) * ratioStarPostsToSteelPine) / (ratioStarPostsToSteelPine + 1);
    const steelWoodValue: number = ((lengthOfTheFence / (ratioStarPostsToSteelPine + 1)) * 1) / (ratioStarPostsToSteelPine + 1);


    this.summaryNumberOfRollsRequired = {
      standarddWire: (lengthOfTheFence * standardWireRowRequired) / standardWireRollLength,
      barbWire: (lengthOfTheFence * barbWireRequired) / barbWireRollLength,
      wallabyProofMesh: isWallabyProof ? lengthOfTheFence / wallabyProofMeshLength : 0,
      star: Math.ceil(starValue),
      steelWood: Math.ceil(steelWoodValue)
    }

  }

  // Manual trigger on keydown on specific fields
  inputChangePopulateFormDataHandler() {
    if (this.selectedFencingType && this.selectedFencingType?.trim().length > 0) {
      this.populateSummaryTableData(this.fencingMaterialTableInformation[this.selectedFencingType]);
    }
  }

  passValuesToParent() {
    this.parentEventTransmitter.emit({
      stage: 2,
      values: {
        "selectedFencingType": this.selectedFencingType,

        "fenceSize": this.fenceSize(),

        "lengthOfFenceRequired": this.materialTreeProtectionForm.controls["lengthOfFenceRequired"].value,
        "postSpacing": this.materialTreeProtectionForm.controls["postSpacing"].value,
        "ratioStarPostToSteel": this.materialTreeProtectionForm.controls["ratioStarPostToSteel"].value,
        "standardWireRows": this.materialTreeProtectionForm.controls["standardWireRows"].value,
        "barbWireRows": this.materialTreeProtectionForm.controls["barbWireRows"].value,

        "standarddWire": this.summaryNumberOfRollsRequired.standarddWire,
        "barbWire": this.summaryNumberOfRollsRequired.barbWire,
        "wallabyProofMesh": this.summaryNumberOfRollsRequired.wallabyProofMesh,
        "star": this.summaryNumberOfRollsRequired.star,
        "steelWood": this.summaryNumberOfRollsRequired.steelWood,
      }
    })
  }

}
